import { useQuery } from '@tanstack/react-query'
import { api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { WEEK_ORDER } from '@/shared/lib/time'
import type { Week, WorkingDay } from '@/shared/api/types'

export const hoursApi = {
  get: (signal?: AbortSignal) => api.get<Week>('/working-hours', signal),
  save: (body: Week) => api.put<Week>('/working-hours', body),
}

export function useWorkingHours() {
  return useQuery({
    queryKey: queryKeys.workingHours,
    queryFn: ({ signal }) => hoursApi.get(signal),
  })
}

/** По умолчанию: будни с девяти до восемнадцати, выходные закрыты. */
export function defaultWeek(): Week {
  return WEEK_ORDER.map((weekday) => ({
    weekday,
    openMin: 9 * 60,
    closeMin: 18 * 60,
    closed: weekday === 0 || weekday === 6,
  }))
}

/**
 * Переставляет неделю в порядок показа — с понедельника.
 *
 * Сервер отдаёт дни по возрастанию, то есть с воскресенья: так устроен
 * time.Weekday. Порядок показа — забота экрана, поэтому перестановка
 * живёт здесь, а не в API.
 */
export function inDisplayOrder(week: Week): Week {
  const byDay = new Map(week.map((day) => [day.weekday, day]))
  return WEEK_ORDER.map(
    (weekday) =>
      byDay.get(weekday) ?? {
        weekday,
        openMin: 9 * 60,
        closeMin: 18 * 60,
        closed: true,
      },
  )
}

/** Короткая сводка вроде «Пн–Пт, 09:00–18:00» для карточки на панели. */
export function summarize(week: Week): string {
  const open = week.filter((day) => !day.closed)
  if (open.length === 0) return 'Звонки выключены'

  const first = open[0] as WorkingDay
  const sameHours = open.every(
    (day) => day.openMin === first.openMin && day.closeMin === first.closeMin,
  )
  if (!sameHours) return 'Часы отличаются по дням'

  return `${daysLabel(open)}, ${formatMinutes(first.openMin)}–${formatMinutes(first.closeMin)}`
}

function daysLabel(days: WorkingDay[]): string {
  const set = new Set(days.map((d) => d.weekday))
  const weekdays = [1, 2, 3, 4, 5].every((d) => set.has(d))
  const weekend = set.has(0) && set.has(6)

  if (weekdays && weekend) return 'Ежедневно'
  if (weekdays && set.size === 5) return 'Пн–Пт'
  return `${days.length} ${plural(days.length, 'день', 'дня', 'дней')} в неделю`
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

function formatMinutes(total: number): string {
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}`
}
